import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';

// Self-contained mini spreadsheet calculator component
export const MiniSpreadsheet = ({ show }) => {
  const [spreadsheetData, setSpreadsheetData] = useState(
    Array(10).fill(null).map(() => ['', '', '', '', ''])
  );
  const [editingCell, setEditingCell] = useState(null);
  const [formulaMode, setFormulaMode] = useState(false);

  const evaluateCell = (value, data) => {
    if (!value || typeof value !== 'string') return value;
    if (!value.startsWith('=')) return value;
    const formula = value.substring(1).trim();
    if (formula === '' || formula.length < 2) return value;
    try {
      let expr = formula.toUpperCase();
      // Replace SUM first before cell refs
      expr = expr.replace(/SUM\(([A-E])(\d+):([A-E])(\d+)\)/g, (match, col1, row1, col2, row2) => {
        const c1 = col1.charCodeAt(0) - 65;
        const c2 = col2.charCodeAt(0) - 65;
        const r1 = parseInt(row1) - 1;
        const r2 = parseInt(row2) - 1;
        let sum = 0;
        for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
          for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
            if (r >= 0 && r < data.length && c >= 0 && c < 5) {
              const val = parseFloat(String(data[r][c]).replace(/[^\d.-]/g, ''));
              if (!isNaN(val)) sum += val;
            }
          }
        }
        return sum;
      });
      // Replace cell references
      expr = expr.replace(/([A-E])(\d+)/g, (match, col, row) => {
        const colIndex = col.charCodeAt(0) - 65;
        const rowIndex = parseInt(row) - 1;
        if (rowIndex >= 0 && rowIndex < data.length && colIndex >= 0 && colIndex < 5) {
          const cellVal = data[rowIndex][colIndex];
          if (typeof cellVal === 'string' && cellVal.startsWith('=')) {
            const evaluated = evaluateCell(cellVal, data);
            const num = parseFloat(String(evaluated).replace(/[^\d.-]/g, ''));
            return isNaN(num) ? 0 : num;
          }
          const num = parseFloat(String(cellVal).replace(/[^\d.-]/g, ''));
          return isNaN(num) ? 0 : num;
        }
        return 0;
      });
      if (!/^[\d\s\+\-\*\/\(\)\.]+$/.test(expr)) return value;
      const result = Function('"use strict";return (' + expr + ')')();
      return isNaN(result) ? value : Math.round(result * 100) / 100;
    } catch (e) {
      return value;
    }
  };

  const updateSpreadsheetCell = (rowIndex, colIndex, value) => {
    setSpreadsheetData(prev => {
      const newData = prev.map(row => [...row]);
      newData[rowIndex][colIndex] = value;
      return newData;
    });
  };

  const clearSpreadsheet = () => {
    setSpreadsheetData(Array(10).fill(null).map(() => ['', '', '', '', '']));
    setEditingCell(null);
    setFormulaMode(false);
  };

  const handleCellClick = (rowIdx, colIdx, cellKey) => {
    if (formulaMode && editingCell && editingCell !== cellKey) {
      const colLetter = String.fromCharCode(65 + colIdx);
      const cellRef = `${colLetter}${rowIdx + 1}`;
      const [editRow, editCol] = editingCell.split('-').map(Number);
      const currentValue = spreadsheetData[editRow]?.[editCol] || '';
      updateSpreadsheetCell(editRow, editCol, currentValue + cellRef);
      return true;
    }
    return false;
  };

  if (!show) return null;

  return (
    <div className="mt-2 border border-gray-200 rounded bg-white shadow-sm">
      <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border-b text-[8px]">
        <span className="font-medium text-gray-600">Reiknivél</span>
        <button onClick={clearSpreadsheet} className="text-gray-400 hover:text-red-500" title="Hreinsa">
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      <table className="w-full text-[9px]">
        <thead>
          <tr className="bg-gray-50">
            <th className="w-5 px-1 py-0.5 border-r"></th>
            {['A', 'B', 'C', 'D', 'E'].map(c => (
              <th key={c} className="w-14 px-1 py-0.5 text-center text-gray-500 font-medium border-r">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spreadsheetData.map((row, ri) => (
            <tr key={ri} className="border-t">
              <td className="px-1 py-0 text-center text-gray-400 bg-gray-50 border-r text-[8px]">{ri + 1}</td>
              {row.map((cell, ci) => {
                const ck = `${ri}-${ci}`;
                const isEd = editingCell === ck;
                const dv = isEd ? cell : (cell.toString().startsWith('=') ? evaluateCell(cell, spreadsheetData) : cell);
                return (
                  <td
                    key={ci}
                    className={`p-0 border-r ${formulaMode && editingCell && editingCell !== ck ? 'cursor-pointer hover:bg-green-50' : ''}`}
                    onClick={() => handleCellClick(ri, ci, ck)}
                  >
                    <input
                      type="text"
                      value={dv}
                      onChange={e => {
                        updateSpreadsheetCell(ri, ci, e.target.value);
                        setFormulaMode(e.target.value.startsWith('='));
                      }}
                      onFocus={() => {
                        setEditingCell(ck);
                        setFormulaMode(cell.toString().startsWith('='));
                      }}
                      onBlur={() => setTimeout(() => { if (!formulaMode) setEditingCell(null); }, 100)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === 'Escape') {
                          setEditingCell(null);
                          setFormulaMode(false);
                          e.target.blur();
                        }
                      }}
                      className="w-full h-5 px-1 text-[9px] border-0 focus:ring-1 focus:ring-green-300 focus:outline-none"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-2 py-1 bg-gray-50 border-t text-[7px] text-gray-500">
        Formúlur: =A1+B1, =SUM(A1:A5) | Smelltu á reit til að velja
      </div>
    </div>
  );
};

export default MiniSpreadsheet;
